import { useCallback, useMemo, useState } from 'react';
import {
  BaseRecord,
  CrudFilter,
  CrudFilters,
  CrudSort,
  MetaQuery,
  useDataProvider,
} from '@refinedev/core';

export type TableResourceLoader<TData extends BaseRecord = BaseRecord> = {
  fetchAll: () => Promise<TData[]>;
  isFetching: boolean;
};

type UseTableResourceLoaderOptions<TData extends BaseRecord = BaseRecord> = {
  resource: string;
  filters?: CrudFilters;
  permanentFilters?: CrudFilters;
  sorters?: CrudSort[];
  permanentSorters?: CrudSort[];
  pageSize?: number;
  meta?: MetaQuery;
  dataProviderName?: string;
};

const serializeFilterValue = (value: unknown) => {
  if (value === undefined) return 'undefined';
  return JSON.stringify(value, (_, parsedValue) => {
    if (typeof parsedValue === 'bigint') {
      return parsedValue.toString();
    }
    return parsedValue;
  });
};

const serializeFilter = (filter: CrudFilter): string => {
  if ('field' in filter) {
    const valueKey = serializeFilterValue(filter.value);
    return `${String(filter.field)}-${filter.operator}-${valueKey}`;
  }

  const nestedValueKey: string = filter.value
    .map(innerFilter => serializeFilter(innerFilter))
    .join('|');
  return `${String(filter.key ?? 'conditional')}-${filter.operator}-${nestedValueKey}`;
};

const serializeSorter = (sorter: CrudSort) =>
  `${String(sorter.field)}-${sorter.order ?? 'undefined'}`;

const dedupeFilters = (filters: CrudFilters): CrudFilters => {
  const seen = new Set<string>();
  return filters.filter(filter => {
    const key = serializeFilter(filter);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const dedupeSorters = (sorters: CrudSort[]): CrudSort[] => {
  const seen = new Set<string>();
  return sorters.filter(sorter => {
    const key = serializeSorter(sorter);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const useTableResourceLoader = <TData extends BaseRecord = BaseRecord>({
  resource,
  filters = [],
  permanentFilters = [],
  sorters = [],
  permanentSorters = [],
  pageSize = 99999,
  meta,
  dataProviderName,
}: UseTableResourceLoaderOptions<TData>): TableResourceLoader<TData> => {
  const getDataProvider = useDataProvider();
  const [isFetching, setIsFetching] = useState(false);

  const combinedFilters = useMemo(
    () => dedupeFilters([...(permanentFilters ?? []), ...(filters ?? [])]),
    [permanentFilters, filters],
  );

  const combinedSorters = useMemo(
    () =>
      dedupeSorters([...(permanentSorters ?? []), ...(sorters ?? [])]).map(sorter => ({
        ...sorter,
      })),
    [permanentSorters, sorters],
  );

  const fetchAll = useCallback(async () => {
    setIsFetching(true);
    try {
      const provider = getDataProvider(dataProviderName);
      const { data } = await provider.getList<TData>({
        resource,
        filters: combinedFilters,
        sorters: combinedSorters,
        pagination: {
          currentPage: 1,
          pageSize,
        },
        meta,
      });
      return data;
    } finally {
      setIsFetching(false);
    }
  }, [
    combinedFilters,
    combinedSorters,
    dataProviderName,
    getDataProvider,
    meta,
    pageSize,
    resource,
  ]);

  return {
    fetchAll,
    isFetching,
  };
};
